import _ from 'lodash';

import { useState } from 'react';

import * as api from '../../api';

import { format } from '../../utils/location';
import { useTranslation } from 'react-i18next';

interface ReturnValues {
  missingUnit: boolean;
  pending: boolean;
  verify: (address: api.Places.PlaceDetails) => void;
  suggestedAddress?: api.Places.PlaceDetails;
  updatedAddress?: api.Places.PlaceDetails;
  error?: string;
}

export const useVerifyAddress = (
  onAddressMatched?: (address: api.Places.PlaceDetails) => void,
): ReturnValues => {
  const { t } = useTranslation('common');
  const [updatedAddress, setUpdatedAddress] =
    useState<api.Places.PlaceDetails>();
  const [suggestedAddress, setSuggestedAddress] =
    useState<api.Places.PlaceDetails>();
  const [missingUnit, setMissingUnit] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const clearState = () => {
    setMissingUnit(false);
    setPending(false);
    setError(undefined);
    setSuggestedAddress(undefined);
  };

  const maybeSkipVerify = (address: api.Places.PlaceDetails) => {
    if (
      missingUnit &&
      updatedAddress &&
      format(updatedAddress).toLowerCase() === format(address).toLowerCase()
    ) {
      clearState();
      setSuggestedAddress(address);
      onAddressMatched?.(address);
      return true;
    }
    return false;
  };

  const verify = async (address: api.Places.PlaceDetails) => {
    if (pending) return;
    if (maybeSkipVerify(address)) return;
    clearState();
    setUpdatedAddress(address);
    try {
      const response = await api.client.Address.verify(
        _.omit(address, ['longitude', 'latitude']),
      );
      const {
        address: suggestedAddress,
        missingUnit = false,
        error,
      } = response;
      if (error) {
        setError(_.upperFirst(_.toLower(error)));
        return;
      }
      setMissingUnit(missingUnit);
      if (missingUnit) return;
      if (suggestedAddress) {
        if (
          format(suggestedAddress).toLowerCase() ===
          format(address).toLowerCase()
        ) {
          return onAddressMatched?.(address);
        }
        setSuggestedAddress({
          ...suggestedAddress,
          longitude: null,
          latitude: null,
        });
      }
    } catch (e) {
      setError(t('generalError'));
    } finally {
      setPending(false);
    }
  };

  return {
    updatedAddress,
    suggestedAddress,
    missingUnit,
    verify,
    pending,
    error,
  };
};
